import { useState } from 'react'

import { color, radius } from '../../theme/tokens'
import type { Artifact } from '../../lib/types'
import { PanelCard } from '../ui'

const ICONS: Record<Artifact['kind'], { glyph: string; bg: string; fg: string }> = {
  pix: { glyph: '◫', bg: color.greenTint, fg: color.greenInk },
  boleto: { glyph: '≡', bg: color.blueTint, fg: color.blueInk },
  contrato: { glyph: '✎', bg: color.purpleTint, fg: color.purpleInk },
  promessa: { glyph: '✓', bg: color.blueTint, fg: color.blueInk },
  link: { glyph: '↗', bg: color.blueTint, fg: color.blueInk },
  agendamento: { glyph: '◷', bg: color.amberTint, fg: color.amberInk },
  transferencia: { glyph: '⇄', bg: color.purpleTint, fg: color.purpleInk },
  tabulacao: { glyph: '☰', bg: color.brandTint, fg: color.brandInk },
  simulacao: { glyph: '∑', bg: color.bg, fg: color.inkSubtle },
}

const STATUS_TINTS = {
  ok: { bg: color.greenTint, fg: color.greenInk },
  sent: { bg: color.amberTint, fg: color.amberInk },
  gen: { bg: color.bg, fg: color.inkSubtle },
} as const

/**
 * Artefatos da chamada.
 *
 * Este painel é a contrapartida da regra não editável: a IA não pode dizer que
 * enviou nada que não esteja aqui, com status visível.
 */
export function ArtifactList({ artifacts }: { artifacts: Artifact[] }) {
  const count = artifacts.length

  return (
    <PanelCard
      title="ARTEFATOS DA CHAMADA"
      right={
        <span style={{ fontWeight: 600, color: '#a08558', fontVariantNumeric: 'tabular-nums' }}>
          {count === 0 ? '—' : `${count} ${count === 1 ? 'artefato' : 'artefatos'}`}
        </span>
      }
      style={{ marginBottom: 12 }}
    >
      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {count === 0 ? (
          <div
            style={{
              border: `1px dashed ${color.borderDashed}`,
              borderRadius: radius.lg,
              padding: '20px 14px',
              textAlign: 'center',
              fontSize: 12,
              color: color.inkFaint,
              lineHeight: 1.5,
            }}
          >
            Artefatos gerados na chamada aparecem aqui.
            <br />A IA nunca declara um envio sem que ele apareça nesta lista.
          </div>
        ) : null}

        {artifacts.map((artifact) => (
          <ArtifactCard key={artifact.id} artifact={artifact} />
        ))}
      </div>
    </PanelCard>
  )
}

function ArtifactCard({ artifact }: { artifact: Artifact }) {
  const [copied, setCopied] = useState(false)
  const icon = ICONS[artifact.kind]
  const tint = STATUS_TINTS[artifact.status]

  const copy = async () => {
    if (!artifact.code) return
    try {
      await navigator.clipboard.writeText(artifact.code)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div
      style={{
        border: `1px solid ${color.border}`,
        borderRadius: radius.lg,
        overflow: 'hidden',
        animation: 'mat .38s cubic-bezier(.22,.61,.36,1) both',
        background: color.surface,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          padding: '10px 12px',
          borderBottom: `1px solid ${color.borderSoft}`,
        }}
      >
        <span
          aria-hidden
          style={{
            width: 28,
            height: 28,
            borderRadius: radius.md,
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            background: icon.bg,
            color: icon.fg,
          }}
        >
          {icon.glyph}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600 }}>{artifact.title}</div>
          <div
            style={{ fontSize: 11, color: color.inkSubtle, fontVariantNumeric: 'tabular-nums' }}
          >
            {artifact.meta}
          </div>
        </div>
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            padding: '4px 9px',
            borderRadius: radius.pill,
            whiteSpace: 'nowrap',
            background: tint.bg,
            color: tint.fg,
            transition: 'all .3s ease',
          }}
        >
          {artifact.status_label}
        </span>
      </div>

      {artifact.kind === 'pix' ? (
        <div style={{ padding: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
          {artifact.qr_svg ? (
            <div
              role="img"
              aria-label="QR Code do PIX"
              style={{
                width: 88,
                height: 88,
                flex: 'none',
                border: `1px solid ${color.border}`,
                borderRadius: radius.sm,
                background: '#fff',
                padding: 4,
              }}
              dangerouslySetInnerHTML={{ __html: artifact.qr_svg }}
            />
          ) : null}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{ fontSize: 10.5, fontWeight: 700, color: color.inkSubtle, marginBottom: 4 }}
            >
              PIX COPIA E COLA
            </div>
            <div
              style={{
                fontFamily: "'Roboto Mono', monospace",
                fontSize: 10,
                color: color.ink,
                wordBreak: 'break-all',
                lineHeight: 1.45,
                maxHeight: 44,
                overflow: 'hidden',
              }}
            >
              {artifact.code}
            </div>
            <button
              type="button"
              onClick={copy}
              style={{
                marginTop: 7,
                fontSize: 11,
                fontWeight: 600,
                border: `1.5px solid ${copied ? color.green : color.border}`,
                color: copied ? color.greenInk : color.ink,
                background: '#fff',
                borderRadius: 7,
                padding: '5px 10px',
                cursor: 'pointer',
              }}
            >
              {copied ? 'Copiado ✓' : 'Copiar chave'}
            </button>
          </div>
        </div>
      ) : null}

      {artifact.kind === 'boleto' && artifact.code ? (
        <div style={{ padding: '10px 12px' }}>
          <div
            style={{ fontSize: 10.5, fontWeight: 700, color: color.inkSubtle, marginBottom: 4 }}
          >
            LINHA DIGITÁVEL
          </div>
          <div
            style={{
              fontFamily: "'Roboto Mono', monospace",
              fontSize: 11,
              letterSpacing: '.02em',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {artifact.code}
          </div>
        </div>
      ) : null}

      {artifact.kind === 'link' && artifact.code ? (
        <div style={{ padding: '10px 12px' }}>
          <div
            style={{
              fontFamily: "'Roboto Mono', monospace",
              fontSize: 11,
              wordBreak: 'break-all',
              color: color.blueInk,
            }}
          >
            {artifact.code}
          </div>
        </div>
      ) : null}

      {artifact.progress_pct !== null && artifact.progress_pct !== undefined ? (
        <div style={{ padding: '10px 12px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 10.5,
              fontWeight: 600,
              color: color.inkSubtle,
              marginBottom: 5,
            }}
          >
            <span>{artifact.progress_label}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{artifact.progress_pct}%</span>
          </div>
          <div
            style={{ height: 5, borderRadius: 3, background: color.bg, overflow: 'hidden' }}
          >
            <div
              style={{
                height: '100%',
                background: color.purple,
                transition: 'width .6s ease',
                width: `${artifact.progress_pct}%`,
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  )
}
